import { useState, useCallback } from 'react';
import { searchApi } from '../api/search';
import type { SearchParams, SearchResponse, SearchResult } from '../types/search';

interface UseSearchResult {
  results: SearchResult[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    size: number;
    totalCount: number;
    totalPages: number;
  };
  query: string;
  search: (params: SearchParams) => Promise<void>;
  clearSearch: () => void;
}

export const useSearch = (): UseSearchResult => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [query, setQuery] = useState('');

  const search = useCallback(async (params: SearchParams) => {
    try {
      setLoading(true);
      setError(null);
      setQuery(params.query);

      const response: SearchResponse = await searchApi.search(params);

      setResults(response.results);
      setPagination({
        page: response.page,
        size: response.size,
        totalCount: response.totalCount,
        totalPages: response.totalPages,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Search failed';
      setError(errorMessage);
      setResults([]);
      setPagination({
        page: 0,
        size: 20,
        totalCount: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const clearSearch = useCallback(() => {
    setResults([]);
    setError(null);
    setQuery('');
    setPagination({
      page: 0,
      size: 20,
      totalCount: 0,
      totalPages: 0,
    });
  }, []);

  return {
    results,
    loading,
    error,
    pagination,
    query,
    search,
    clearSearch,
  };
};
